import './UploadStudentListModal.scss';

import { useCallback, useEffect, useState } from 'react';

import { ReactComponent as DocumentIcon } from 'images/icons/menu/Document.svg';
import DropzoneContainer from './DropzoneContainer/DropzoneContainer';
import { Modal } from '@trackman/web-shared-components';
import NoInvitesLeft from 'components/Organization/NoInvitesLeft/NoInvitesLeft';
import NumberOfInvitesLeft from 'components/Organization/NumberOfInvitesLeft/NumberOfInvitesLeft';
import Separator from 'components/Separator/Separator';
import StudentsList from './StudentsList/StudentsList';
import TemplateContainer from './TemplateContainer/TemplateContainer';
import { isValidEmail } from 'utilities/inputValidation';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';

export const UploadStudentListModal = observer(() => {
  const { t } = useTranslation();
  const iss = useStores().inviteStudentsStore;
  const orgStore = useStores().organizationsStore;
  const cs = useStores().classesStore;
  const [errorMsgs, setErrorMsgs] = useState<string[]>([]);

  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);

  useEffect(() => {
    const classItem = cs.classesList.find((classItem) => classItem.id === cs.selectedClassId);
    iss.selectedClass = { id: cs.selectedClassId, name: classItem ? classItem.name : '' };
  }, [iss, cs.classesList, cs.selectedClassId]);

  const validateInvites = useCallback(() => {
    const emailSet = new Set<string>();

    setErrorMsgs([]);

    if (iss.newInvites.length > iss.numberOfInvitesLeft) {
      setErrorMsgs((errorMsg) => [...errorMsg, t('YouHaveReachedTheLimitOfLicenses', { ns: 'studentsPage' })]);
    }

    iss.newInvites.forEach((invite) => {
      if (!isValidEmail(invite.email)) {
        setErrorMsgs((errorMsg) => [...errorMsg, t('EmailIsNotValid', { ns: 'studentsPage', email: invite.email })]);
      }

      if (emailSet.has(invite.email)) {
        setErrorMsgs((errorMsg) => [...errorMsg, t('EmailIsAddedMultipleTimes', { ns: 'studentsPage', email: invite.email })]);
      } else {
        emailSet.add(invite.email);
      }
      invite.classes = [{ ...iss.selectedClass }];
      invite.organisationName = orgStore.selectedOrg.name;
    });
  }, [iss.newInvites, iss.numberOfInvitesLeft, t, iss.selectedClass, orgStore.selectedOrg.name]);

  useEffect(() => {
    iss.newInvites.length && validateInvites();
  }, [iss.newInvites.length, validateInvites]);

  const closeModal = useCallback(() => {
    iss.isImportModalOpen = false;
    iss.newInvites = [];
  }, [iss]);

  return (
    <Modal
      open={iss.isImportModalOpen}
      onClose={() => closeModal()}
      className='organization-modal upload-modal'
      title={t('UploadStudentList', { ns: 'studentsPage' })}
      titleIcon={<DocumentIcon />}
      cancelBtn={{
        label: t('Cancel', { ns: 'general' }),
        onClick: () => closeModal(),
        className: 'styled-button secondary',
      }}
      confirmBtn={{
        label: t('SendInvitation', { ns: 'studentsPage' }),
        onClick: () => {
          iss.submitInvitesAndUpdate(selectedOrganizationId);
          closeModal();
        },
        disabled: !iss.newInvites.length || iss.numberOfInvitesLeft < iss.newInvites.length || errorMsgs.length > 0 || iss.isLoading,
      }}
    >
      <div className='modal-container'>
        {iss.numberOfInvitesLeft < 1 ? (
          <NoInvitesLeft />
        ) : (
          <>
            {iss.newInvites.length === 0 ? (
              <div className='steps-container'>
                <TemplateContainer />
                <Separator />
                <DropzoneContainer validateInvites={validateInvites} />
              </div>
            ) : (
              <StudentsList validateInvites={validateInvites} errorMsgs={errorMsgs} />
            )}
            <NumberOfInvitesLeft />
          </>
        )}
      </div>
    </Modal>
  );
});

export default UploadStudentListModal;
